/**
 * Which model-gated nav entries this node should show.
 *
 * The sidebar's per-model report entries are only useful when the model behind them is
 * actually running on a camera, so the nav asks this hook and drops the rest (see
 * NavNode.requiresModel).
 *
 * Fails OPEN: while the registry is loading, and if the request fails or is denied
 * (`models.view` / `ai-models.view` is not in every role), every gated entry stays visible.
 * A transient 502 or a role without registry access must not silently amputate the nav —
 * hiding a report the operator uses daily is far worse than showing one that turns out empty.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApi, type Model } from '../api';
import { activeModelSurfaces } from '../constants/modelSurfaces';

/** Enough to cover any real registry in one request; the page-level list uses the same bound. */
const FETCH_LIMIT = 500;

export interface ActiveModelSurfaces {
  /** True when `surface` is unlocked — and true for everything until the registry is known. */
  hasModel: (surface: string) => boolean;
  /** False until the registry has been read at least once (or the read failed). */
  loaded: boolean;
  /**
   * The active surfaces as a concrete list, or `null` while the registry is unknown (still
   * loading, or the read failed/was denied).
   *
   * `hasModel` fails OPEN because hiding a nav entry an operator uses daily is worse than
   * showing an empty one. A caller that needs to ENUMERATE the surfaces — the dashboard, which
   * asks the summary endpoint for exactly the models this node runs — cannot use that: an
   * open-failed predicate would have it request every surface and draw a card for models
   * nobody runs, which is the opposite of what dynamic cards mean. So this is null rather
   * than "everything", and the caller decides what to do about not knowing.
   */
  surfaces: string[] | null;
}

/**
 * @param nodeKey Cloud: the selected node id. Changing it refetches, since the registry is
 *                per-node. Local: omit.
 */
export function useActiveModelSurfaces(nodeKey?: string | null): ActiveModelSurfaces {
  const { models: modelsApi } = useApi();
  const [models, setModels] = useState<Model[] | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setModels(null);
    setLoaded(false);
    modelsApi
      .list({ page: 1, limit: FETCH_LIMIT })
      .then((res) => {
        if (cancelled) return;
        setModels(Array.isArray(res?.data) ? res.data : []);
        setLoaded(true);
      })
      .catch(() => {
        // Leave `models` null — hasModel() then keeps every entry visible.
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeKey]);

  const surfaces = useMemo(() => (models ? activeModelSurfaces(models) : null), [models]);

  return useMemo(
    () => ({
      hasModel: (surface: string) => (surfaces ? surfaces.has(surface) : true),
      loaded,
      surfaces: surfaces ? [...surfaces].sort() : null,
    }),
    [surfaces, loaded],
  );
}
